import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../primitives/DeltaPill';
import { useChartTokens } from '../../theme/useChartTokens';
import { ReachCardSummary } from './types';

interface ReachStatProps {
  entityType: 'track' | 'artist';
  summary: ReachCardSummary;
}

// The card reads as an audience, not a score: a compact people count, the
// range delta, and how many playlists carry that audience. An empty series
// is handled by the caller before this renders — never render "0 people".
const ReachStatComponent: React.FC<ReachStatProps> = ({ entityType, summary }) => {
  const intl = useIntl();
  const chart = useChartTokens();
  const isTrack = entityType === 'track';

  const peopleCount = isTrack
    ? intl.formatMessage(
        { id: 'app.sml.track.details.reachCard.peopleCount', defaultMessage: '{count} people' },
        { count: intl.formatNumber(summary.current, { notation: 'compact', maximumFractionDigits: 1 }) }
      )
    : intl.formatMessage(
        { id: 'app.sml.artist.details.reachCard.peopleCount', defaultMessage: '{count} people' },
        { count: intl.formatNumber(summary.current, { notation: 'compact', maximumFractionDigits: 1 }) }
      );

  const playlistsLine = isTrack
    ? intl.formatMessage(
        { id: 'app.sml.track.details.reachCard.playlistsLine', defaultMessage: 'Across {count} playlists' },
        { count: intl.formatNumber(summary.reachPlaylists) }
      )
    : intl.formatMessage(
        { id: 'app.sml.artist.details.reachCard.playlistsLine', defaultMessage: 'Across {count} playlists' },
        { count: intl.formatNumber(summary.reachPlaylists) }
      );

  const unknownAudienceNote = isTrack
    ? intl.formatMessage(
        {
          id: 'app.sml.track.details.reachCard.unknownAudienceNote',
          defaultMessage: '{count} playlists have unknown audience',
        },
        { count: intl.formatNumber(summary.unknownAudiencePlaylists) }
      )
    : intl.formatMessage(
        {
          id: 'app.sml.artist.details.reachCard.unknownAudienceNote',
          defaultMessage: '{count} playlists have unknown audience',
        },
        { count: intl.formatNumber(summary.unknownAudiencePlaylists) }
      );

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span style={{ fontSize: '28px', fontWeight: 600, color: chart.tx0 }}>{peopleCount}</span>
        {summary.previous !== null && <DeltaPill value={summary.delta} />}
      </div>
      <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
        {playlistsLine}
      </p>
      {summary.unknownAudiencePlaylists > 0 && (
        <p className="text-muted mb-0 mt-1" style={{ fontSize: '11px' }}>
          {unknownAudienceNote}
        </p>
      )}
    </div>
  );
};

export const ReachStat = TypedMemo(ReachStatComponent);
